// SEND-156 (Howard ruled 2026-09-01) — THE ONE-PAGE OPENING LIST for the
// supply counter. CONFIRMED openings that carry a TYPED order size, one row
// each. Every figure and every sentence arrives from the server: this page
// composes no size, merges no row and computes no total. No price, no quote,
// no J-channel, no siding figure.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function OpeningListSheet() {
  const { id } = useParams();
  const [sheet, setSheet] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get(`/estimates/${id}/photo-takeoff/opening-list`)
      .then(({ data }) => setSheet(data))
      .catch((e) =>
        setErr(e?.response?.data?.detail || "Could not load the opening list")
      );
  }, [id]);

  if (err)
    return (
      <div className="p-8 text-sm text-red-700" data-testid="opening-list-error">
        {err}
      </div>
    );
  if (!sheet)
    return (
      <div className="p-8 flex items-center gap-2 text-sm" data-testid="opening-list-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading opening list…
      </div>
    );

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white text-[#1a1a1a]" data-testid="opening-list-sheet">
      <div className="no-print flex justify-end mb-4">
        <button
          className="inline-flex items-center gap-2 border border-black px-3 py-1.5 text-sm font-bold uppercase tracking-wider"
          onClick={() => window.print()}
          data-testid="opening-list-print-btn"
        >
          <Printer className="w-4 h-4" /> Print / save as PDF
        </button>
      </div>

      <div className="border-b-2 border-black pb-3 mb-4">
        <div className="text-xl font-bold" data-testid="opening-list-header">
          OPENING LIST — {sheet.estimate_number || sheet.estimate_id}
        </div>
        <div className="text-sm mt-1">
          {sheet.customer_name}
          {sheet.address ? ` · ${sheet.address}` : ""}
        </div>
      </div>

      {sheet.groups?.length ? (
        <div data-testid="opening-list-table">
          {sheet.groups.map((g) => (
            <div key={g.heading} className="mb-5" data-testid={`opening-list-group-${g.heading.replace(/\s+/g, "-")}`}>
              <div className="text-sm font-bold uppercase tracking-wider border-b-2 border-black pb-1 mb-1"
                data-testid={`opening-list-heading-${g.heading.replace(/\s+/g, "-")}`}>
                {g.heading}
                {/* SEND-165 — THE SHEET SAYS IT WAS HIM, not a read. */}
                {g.by_hand ? (
                  <span className="ml-2 text-[10px] font-bold tracking-wider"
                    data-testid={`opening-list-heading-by-hand-${g.heading.replace(/\s+/g, "-")}`}>
                    · TAGGED BY HAND{g.rows?.[0]?.elevation_by ? ` (${g.rows[0].elevation_by})` : ""}
                  </span>
                ) : null}
              </div>
              {g.note ? (
                <div className="text-[10px] text-[#555] mb-1">{g.note}</div>
              ) : null}
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-black text-left">
                    {sheet.columns.map((c) => (
                      <th key={c} className="py-1 pr-3 text-[11px] font-bold uppercase tracking-wider">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r, i) => (
                    <tr key={i} className="border-b border-[#ddd]" data-testid={`opening-list-row-${r.photo_key}-${i}`}>
                      <td className="py-1 pr-3 tabular-nums">{r.qty}</td>
                      <td className="py-1 pr-3 font-bold whitespace-nowrap" data-testid={`opening-list-size-${r.photo_key}-${i}`}>
                        {r.size}
                      </td>
                      <td className="py-1 pr-3 text-[11px] font-bold uppercase tracking-wider" data-testid={`opening-list-kind-${r.photo_key}-${i}`}>
                        {r.kind}
                      </td>
                      <td className="py-1 pr-3">
                        {/* SEND-165 — HIS TYPED NAME, not the word "opening". */}
                        {r.name ? (
                          <span className="font-bold" data-testid={`opening-list-name-${r.photo_key}-${i}`}>
                            {r.name}
                          </span>
                        ) : (
                          <span data-testid={`opening-list-style-${r.photo_key}-${i}`}>{r.style}</span>
                        )}
                        {r.name && r.style_typed && r.style !== r.name ? (
                          <span className="ml-1 text-[10px] text-[#777]">· {r.style}</span>
                        ) : null}
                        {g.heading === "OTHER PHOTOS" ? (
                          <span className="ml-1 text-[10px] uppercase tracking-wider text-[#777]">· {r.photo}</span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {sheet.elevations_empty_note ? (
            <div className="text-[10px] text-[#555]" data-testid="opening-list-empty-elevations">
              {sheet.elevations_empty_note}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="text-sm" data-testid="opening-list-empty">
          {sheet.held_note ||
            "no confirmed opening on this job carries a size you typed — nothing is printed and no size is invented"}
        </div>
      )}

      {/* SEND-159 — the duplicate risk is NAMED on the sheet and never
          resolved: the candidates are listed, nothing is merged and no count
          is done. */}
      {sheet.duplicate_note ? (
        <div className="mt-4 border border-black p-2" data-testid="opening-list-duplicate-warning">
          <div className="text-[11px] font-bold">{sheet.duplicate_note}</div>
          {sheet.duplicate_candidates?.length ? (
            <div className="mt-1">
              <div className="text-[10px] text-[#555]">{sheet.duplicate_candidates_note}</div>
              <ul className="mt-1 text-[11px] space-y-1">
                {sheet.duplicate_candidates.map((c, i) => (
                  <li key={i} data-testid={`opening-list-duplicate-${i}`}>
                    <span className="font-bold">{c.heading} · {c.size}</span>
                    {c.rows.map((r, j) => (
                      <span key={j} className="block pl-3 text-[10px]">
                        · {r.kind} — {r.name || r.style}
                        {r.photo ? ` (${r.photo})` : ""}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {sheet.held_rows?.length ? (
        <div className="mt-6" data-testid="opening-list-held">
          <div className="text-[11px] font-bold uppercase tracking-wider border-b border-black pb-1 mb-2">
            Not on this sheet
          </div>
          <div className="text-[11px] mb-2">{sheet.held_note}</div>
          <ul className="text-[11px] space-y-1">
            {sheet.held_rows.map((h, i) => (
              <li key={i} data-testid={`opening-list-held-row-${i}`}>
                · {h.kind} — {h.name || h.style} ({h.photo}): {h.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-6 text-[10px] text-[#555] border-t pt-2" data-testid="opening-list-note">
        {sheet.note}
      </div>
    </div>
  );
}
